import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { api, authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"

export type UnitStatus = "active" | "in_repair" | "damaged" | "missing_docs"

export const UNIT_EMPTY_FORM = {
  code: "", name: "", unitTypeId: "", licensePlate: "",
  vin: "", brand: "", model: "", engineNumber: "", manufactureYear: "",
  unitNumber: "", operatorId: "", fuelType: "" as "diesel" | "gnc" | "",
  status: "active" as UnitStatus, hasSatellite: false, location: "", observations: "",
  // specs
  tare: "", axles: "", lengthM: "", widthM: "", heightM: "",
  sealCount: "", refrigerationUnitId: "", tempSensorCount: "",
  isMapped: false, isCalibrated: false,
}

export type UnitFormState = typeof UNIT_EMPTY_FORM

export interface UnitSpecs {
  tare: number | null
  axles: string | null
  lengthM: number | null
  widthM: number | null
  heightM: number | null
  sealCount: number | null
  refrigerationUnitId: string | null
  tempSensorCount: number | null
  isMapped: boolean | null
  isCalibrated: boolean | null
}

export interface UnitRow {
  id: string
  code: string
  name: string
  sgtCodes: string[] | null
  unitTypeId: string
  unitTypeCode: string
  unitTypeName: string
  licensePlate: string | null
  vin: string | null
  brand: string | null
  model: string | null
  engineNumber: string | null
  manufactureYear: number | null
  unitNumber: number | null
  operatorId: string | null
  operatorName: string | null
  fuelType: "diesel" | "gnc" | null
  status: UnitStatus
  hasSatellite: boolean
  location: string | null
  observations: string | null
  specs: UnitSpecs | null
  inMobile: boolean
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

interface Options {
  onCreateSuccess?: () => void
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
  onError?: (err: Error) => void
}

export function useUnitMutations(editingId: string | null, opts: Options = {}) {
  const qc = useQueryClient()

  const invalidate = () => qc.invalidateQueries({ queryKey: ["units"] })

  const buildPayload = (data: UnitFormState) => {
    const tare = data.tare ? parseInt(data.tare) : null
    const axles = data.axles || null
    const lengthM = data.lengthM ? parseFloat(data.lengthM) : null
    const widthM = data.widthM ? parseFloat(data.widthM) : null
    const heightM = data.heightM ? parseFloat(data.heightM) : null
    const sealCount = data.sealCount ? parseInt(data.sealCount) : null
    const refrigerationUnitId = data.refrigerationUnitId || null
    const tempSensorCount = data.tempSensorCount ? parseInt(data.tempSensorCount) : null

    // Only include specs if at least one field is set
    const specs = (tare || axles || lengthM || widthM || heightM || sealCount || refrigerationUnitId || tempSensorCount || data.isMapped || data.isCalibrated)
      ? { tare, axles, lengthM, widthM, heightM, sealCount, refrigerationUnitId, tempSensorCount, isMapped: data.isMapped, isCalibrated: data.isCalibrated }
      : undefined

    return {
      code: data.code, name: data.name,
      unitTypeId: data.unitTypeId,
      licensePlate: data.licensePlate || null,
      vin: data.vin || null,
      brand: data.brand || null,
      model: data.model || null,
      engineNumber: data.engineNumber || null,
      manufactureYear: data.manufactureYear ? parseInt(data.manufactureYear) : null,
      unitNumber: data.unitNumber ? parseInt(data.unitNumber) : null,
      operatorId: data.operatorId || null,
      fuelType: (data.fuelType as "diesel" | "gnc" | null) || null,
      status: data.status || "active",
      hasSatellite: data.hasSatellite,
      location: data.location || null,
      observations: data.observations || null,
      specs,
    }
  }

  const createMut = useMutation({
    mutationFn: async (data: UnitFormState) => {
      const res = await api["units"].$post({ json: buildPayload(data) })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al crear unidad")
      }
      return res.json()
    },
    onSuccess: () => {
      invalidate()
      toast.success("Unidad creada exitosamente")
      opts.onCreateSuccess?.()
    },
    onError: (err: Error) => opts.onError?.(err),
  })

  const updateMut = useMutation({
    mutationFn: async (data: UnitFormState) => {
      if (!editingId) throw new Error("Sin unidad seleccionada")
      const res = await api["units"][":id"].$patch({
        param: { id: editingId },
        json: buildPayload(data),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar unidad")
      }
      return res.json()
    },
    onSuccess: () => {
      invalidate()
      toast.success("Unidad actualizada exitosamente")
      opts.onUpdateSuccess?.()
    },
    onError: (err: Error) => opts.onError?.(err),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api["units"][":id"].$delete({ param: { id } })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar unidad")
      }
    },
    onSuccess: () => {
      invalidate()
      toast.success("Unidad eliminada exitosamente")
      opts.onDeleteSuccess?.()
    },
    onError: (err: Error) => opts.onError?.(err),
  })

  const restoreMut = useMutation({
    mutationFn: (id: string) =>
      authorizedFetch(`${API_URL}/api/units/${id}/restore`, { method: "PATCH" }),
    onSuccess: () => {
      invalidate()
      toast.success("Unidad restaurada exitosamente")
      opts.onDeleteSuccess?.()
    },
    onError: (err: Error) => opts.onError?.(err),
  })

  return { createMut, updateMut, deleteMut, restoreMut }
}
